"use client";
import { Marker } from "react-map-gl";

interface UserLocationMarkerProps {
  longitude: number;
  latitude: number;
  /**
   * Show pulsing animation
   * @default true
   */
  showPulse?: boolean;
  /**
   * Accuracy radius in meters (optional)
   */
  accuracy?: number;
}

/**
 * User location marker component
 *
 * Displays the user's current location as a blue dot with optional pulsing ring,
 * similar to Google Maps and Apple Maps.
 */
export function UserLocationMarker({ longitude, latitude, showPulse = true, accuracy }: UserLocationMarkerProps) {
  return (
    <Marker longitude={longitude} latitude={latitude} anchor="center">
      <div className="relative flex items-center justify-center">
        {/* Pulsing ring */}
        {showPulse && (
          <div
            className="absolute h-12 w-12 animate-[ping_2s_ease-in-out_infinite] rounded-full bg-brand-primary opacity-30"
            style={{ animationDelay: "0s" }}
          />
        )}

        {/* Outer white ring */}
        <div className="relative z-10 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-lg">
          {/* Inner blue dot */}
          <div className="h-3 w-3 rounded-full bg-brand-primary" />
        </div>

        {/* Optional accuracy circle
            Note: This is a simplified visual indicator, not geographically accurate.
            For a production implementation, you'd need to project the accuracy radius
            based on the map's zoom level and projection. Current formula: 1m ≈ 0.5px, capped at 100px. */}
        {accuracy && accuracy > 0 && (
          <div
            className="pointer-events-none absolute rounded-full border border-brand-primary/20 bg-brand-primary/10"
            style={{
              width: `${Math.min(accuracy / 2, 100)}px`,
              height: `${Math.min(accuracy / 2, 100)}px`,
            }}
          />
        )}
      </div>
    </Marker>
  );
}
